import { Box, Button, Grid, Typography } from '@mui/material'
import { NewFileAttachments } from 'components/TaskDrawer/NewFileAttachments'
import AttachFilesInput from 'components/ui/attach-files-input'
import React, { FC, useEffect, useState } from 'react'
import type { RichTextEditorRef } from 'mui-tiptap'
import SubmittingFor from './SubmittingFor'
import { OrganizationTableItem, SelectedTask } from 'types'
import { SelectedFile } from '../Comments/types'
import { Editor } from '@tiptap/core'

type Props = {
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveFile: (i: any) => void
  selectedFiles: SelectedFile[]
  onSave: (html: string) => void
  selectedData: SelectedTask | OrganizationTableItem
  isEditing?: boolean
  onCancel: () => void
  onClear: () => void
  keepExpanded: () => void
  editorInstance: Editor
  rteRef: React.RefObject<RichTextEditorRef>
}

const MAX_PREVIEWS = 8

function useEditorActive(rteRef: React.RefObject<RichTextEditorRef>) {
  const [active, setActive] = useState(false)
  useEffect(() => {
    const ed = rteRef.current?.editor
    if (!ed) return
    const onFocus = () => setActive(true)
    const onBlur = () => setActive(false)
    ed.on('focus', onFocus)
    ed.on('blur', onBlur)
    return () => {
      ed.off('focus', onFocus)
      ed.off('blur', onBlur)
    }
  }, [rteRef])
  return active
}

const TextEditorFooter: FC<Props> = ({
  isEditing = false,
  keepExpanded,
  onSave,
  onUpload,
  onCancel,
  onClear,
  rteRef,
  selectedFiles,
  selectedData,
  onRemoveFile,
}) => {
  const editorActive = useEditorActive(rteRef)
  const mode: 'idle' | 'compose' | 'edit' = isEditing ? 'edit' : editorActive ? 'compose' : 'idle'
  const html = rteRef.current?.editor?.getHTML() ?? ''
  const isEmpty = !html || /^<p>(<br>)?<\/p>$/.test(html)

  const visible = selectedFiles.slice(0, MAX_PREVIEWS)
  const overflow = Math.max(0, selectedFiles.length - visible.length)

  const handleCommit = async () => {
    const h = rteRef.current?.editor?.getHTML() ?? ''
    await onSave(h)
    onClear()
  }

  return (
    <Grid container className="rte-footer">
      <Box
        onMouseDown={keepExpanded}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          py: 1,
          px: 1.5,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {/* LEFT: attachments only */}
        <Box
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            whiteSpace: 'nowrap',
            flexShrink: 1,
          }}
        >
          {visible.length > 0 && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <NewFileAttachments selectedFiles={visible} onRemoveFile={onRemoveFile} size={28} />
            </Box>
          )}

          {overflow > 0 && (
            <Box
              aria-label={`+${overflow} more`}
              sx={{
                flex: '0 0 auto',
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.selected',
                color: 'text.primary',
                fontSize: 12,
                fontWeight: 600,
                ml: 0.5,
              }}
            >
              +{overflow}
            </Box>
          )}
        </Box>

        {/* RIGHT: actions (same row, pushed to far right) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            ml: 'auto', // <-- pushes this box to the far right
            flexShrink: 0,
            whiteSpace: 'nowrap',
            '& .MuiButton-root': { whiteSpace: 'nowrap' },
          }}
        >
          {mode === 'edit' ? (
            <>
              <Button variant="contained" size="small" onMouseDown={e => e.stopPropagation()} onClick={handleCommit}>
                Update
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  onCancel()
                  onClear()
                }}
              >
                Cancel
              </Button>
              <Typography variant="caption" sx={{ color: 'text.warning', ml: 0.5 }}>
                Updates currently accept text only
              </Typography>
            </>
          ) : (
            <>
              {selectedData && (
                <Box
                  sx={{
                    maxWidth: 260,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={selectedData?.name || 'Submitting for'}
                >
                  <SubmittingFor selectedData={selectedData} />
                </Box>
              )}
              <Button
                variant="contained"
                size="small"
                disabled={isEmpty}
                onMouseDown={e => e.stopPropagation()}
                onClick={handleCommit}
              >
                Post
              </Button>
              <AttachFilesInput onUpload={onUpload} />
            </>
          )}
        </Box>
      </Box>
    </Grid>
  )
}

export default TextEditorFooter
